import { validate } from "capitalrx-components-2.0";
import { FileEntry } from "capitalrx-components-2.0/lib/forms";
import { ButtonAppearance, Buttons, Color } from "capitalrx-components-2.0/lib/general";
import { URL, CLIENT_URL } from "@/constants/urls";
import { ProviderManagement } from "@/api/interfaces";

export const numberValidator = (value: string) => {
  if (isNaN(+value)) return "Input requires numbers only";
};

export function snakeCaseToTitleCase(string: string) {
  return string
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Formats input to allow only numbers and decimal for currency values
 * Strips non-numeric characters
 * Allows only one decimal point
 * Limits to two decimal places
 */
export const formatPaidAmount = (value: string): string => {
  let cleaned = (value ?? "").replace(/[^0-9.]/g, "");

  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  if (parts.length === 2 && parts[1].length > 2) {
    cleaned = parts[0] + "." + parts[1].slice(0, 2);
  }

  return cleaned;
}

/**
 * Formats "1234567890[ext]" to "123-456-7890 x 123"
 * Strips non-digits; phone max 10, ext max 6; auto-inserts dashes and ext marker
 */
export function formatPhoneWithExtension(value: string): string {
  const digits = (value || "").replace(/\D/g, "");

  const phoneDigits = digits.slice(0, 10);
  const extDigits = digits.slice(10, 16);

  const a = phoneDigits.slice(0, 3);
  const b = phoneDigits.slice(3, 6);
  const c = phoneDigits.slice(6, 10);

  let formatted = "";
  if (phoneDigits.length === 0) {
    formatted = "";
  } else if (phoneDigits.length <= 3) {
    formatted = a;
  } else if (phoneDigits.length <= 6) {
    formatted = `${a}-${b}`;
  } else {
    formatted = `${a}-${b}-${c}`;
  }

  if (phoneDigits.length === 10 && extDigits.length > 0) {
    formatted += ` x ${extDigits}`;
  }

  return formatted;
}

/**
 * Formats "12345[1234]" to "12345-1234".
 * Strips non-digits; max 9 digits; inserts dash after 5.
 */
export function formatZipCode(value: string): string {
  const digits = (value || "").replace(/\D/g, "").slice(0, 9);
  const a = digits.slice(0, 5);
  const b = digits.slice(5, 9);
  if (digits.length <= 5) return a;
  return `${a}-${b}`;
}

/**
 * Strips formatting from phone number with optional extension
 * Converts "123-456-7890 x 123" to "1234567890x123"
 * Keeps only digits and 'x' for extension marker
 */
export function stripPhoneFormatting(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, "");
  const phoneDigits = digits.slice(0, 10);
  const extDigits = digits.slice(10, 16);

  if (extDigits.length > 0) {
    return phoneDigits + "x" + extDigits;
  }
  return phoneDigits;
}

/**
 * Validates that the input is a valid paid amount
 * Allows empty string or number up to two decimal places
 */
export const validatePaidAmount = (value: string) => {
  if (!value) return true;
  if (value === ".") {
    return "Please enter a valid amount";
  }
  if (!/^\d+(\.\d{2,2})?$/.test(value)) {
    return "Please enter a valid amount";
  }
  return true;
}

/**
 * validates US phone number (10 digits) with optional extension
 * accepts formats like:
 * - 555-555-5555
 * - 555-555-5555 x1234
 * - 555-555-5555 ext 1234
 */
export const validatePhoneNumber = (value: string) => {
  if (!value) return true;

  const cleaned = value.trim();

  // extract extension if present (supports x, ext, extension, #)
  const extensionMatch = cleaned.match(/\s+(?:x|ext|extension|#)\s*(\d+)$/i);

  // remove extension to isolate the base number
  let baseNumber = cleaned;
  if (extensionMatch) {
    baseNumber = baseNumber.substring(0, extensionMatch.index);
  }

  // extract only digits from base number
  const digitsOnly = baseNumber.replace(/\D/g, "");

  if (digitsOnly.length !== 10) {
    return "Phone number must be 10 digits (excluding extension)";
  }

  return true;
};

export const npiValidator = (value: string): string | undefined => {
  if (!value) {
    return "NPI is required";
  }
  if (!/^\d+$/.test(value)) {
    return "NPI must contain only digits";
  }
  if (value.length !== 10) {
    return "NPI must be exactly 10 digits";
  }
};

export const isDate = (value: string) => {
  if (!value) {
    return true;
  }
  return validate.isDate(value, "Invalid Date. Must be mm/dd/yyyy");
};

export const currentEnvironment: string = site?.environment || "";
export const isProduction: boolean = currentEnvironment === "prod";
export const isPrime: boolean = currentEnvironment.includes("prime") || currentEnvironment == "training";

export const transformNetworkData = (data: ProviderManagement.ProviderNetworkListSearchResponse): ProviderManagement.NetworkDetails[] => {
  return data?.results
    ?.map((item) => item.provider_network)
    .filter((network): network is ProviderManagement.NetworkDetails => !!network);
};

/**
 * validates equality for two objects, array, or primitive
 * @param x object, array, or primitive
 * @param y object, array, or primitive
 * @param shouldSortArrays boolean - optional, defaults to true. will consider arrays
 * equal if all values are equal, even if presented in differing order
 * @returns boolean
 */

export function deepEqual(x, y, shouldSortArrays = true) {
  if (shouldSortArrays && Array.isArray(x) && Array.isArray(y)) {
    if (x.length !== y.length) {
      return false;
    }
    const sortedX = [...x].sort((a, b) => (JSON.stringify(a) > JSON.stringify(b) ? 1 : -1));
    const sortedY = [...y].sort((a, b) => (JSON.stringify(a) > JSON.stringify(b) ? 1 : -1));
    return sortedX.every((itemX, index) => deepEqual(itemX, sortedY[index], shouldSortArrays));
  }

  if (x && y && typeof x === "object" && typeof x === typeof y) {
    const xKeys = Object.keys(x);
    const yKeys = Object.keys(y);
    return xKeys.length === yKeys.length && xKeys.every((key) => deepEqual(x[key], y[key], shouldSortArrays));
  }

  return x === y;
}

type isEmptyProps = string | number | null | undefined
export function isEmpty(input: isEmptyProps): boolean {
  return (
    input === null
    || input === undefined
    || (typeof input === "string" && input.trim() === "")
    || (typeof input === "number" && isNaN(input))
  );
}


export function isEmptyArray(inputs: isEmptyProps[]): boolean {
  return !inputs.every((item) => !isEmpty(item));
}

export const getClientID = (pathname: string = window.location.pathname) => {
  const pathArray = pathname.split("/");
  const clientIndex = pathArray.indexOf("client");
  const clientId = clientIndex !== -1 ? pathArray[clientIndex + 1] : undefined;
  return clientId?.toLowerCase();
};

export const getThemeURL = (
  pathKey: keyof typeof URL | keyof typeof CLIENT_URL,
): string => {
  const clientId = getClientID();
  const isWhiteLabel = !!clientId;

  if (isWhiteLabel && CLIENT_URL[pathKey as keyof typeof CLIENT_URL]) {
    return CLIENT_URL[pathKey as keyof typeof CLIENT_URL].replace(":Client", clientId!);
  };

  return URL[pathKey as keyof typeof URL];
};

export const formatDateWithSlashes = (date: string): string => {
  return date
    .replace(/\D/g, "") // Remove non-digits
    .slice(0, 8) // Limit to 8 digits
    .replace(/(\d{2})(\d{2})?(\d{0,4})?/, (_, d, m, y) => {
      return [d, m, y].filter(Boolean).join("/");
    });
};

export const capitalize = (str: string): string => {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};


/**
 * Helper function to process files from a specific document category
 */
export const processDocumentFiles = (
  files: FileEntry[],
  notes: string,
  documentType: ProviderManagement.DocumentTypeValue,
): {
  fileObjects: Array<{ file: File; fileName: string }>;
  fileMetadata: ProviderManagement.ProviderDocumentFileUpload[];
} => {
  const fileObjects: Array<{ file: File; fileName: string }> = [];
  const fileMetadata: ProviderManagement.ProviderDocumentFileUpload[] = [];

  files.forEach((fileEntry) => {
    // Skip if no file object is present
    if (!fileEntry.file) return;

    // Store the file object for later use
    fileObjects.push({
      file: fileEntry.file,
      fileName: fileEntry.name,
    });

    // Only send metadata to the API
    fileMetadata.push({
      content_type: fileEntry.type,
      file_name: fileEntry.name,
      file_size: fileEntry.file.size,
      notes: notes,
      document_type: documentType,
    });
  });

  return { fileObjects, fileMetadata };
};


/**
 * Uploads a file directly to S3 using a presigned POST URL
 * @param file The file to upload
 * @param presignedPost The presigned POST data returned from the server
 * @returns Promise resolving to a boolean indicating success
 */
export const uploadFileToS3 = async (file: File, presignedPost: ProviderManagement.PresignedPost): Promise<boolean> => {
  try {
    const formData = new FormData();

    // Add all the fields from the presigned URL response
    Object.entries(presignedPost.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add the file as the last field
    formData.append("file", file);

    // Upload directly to S3
    const response = await fetch(presignedPost.url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`S3 upload failed: ${response.status} - ${errorText}`);
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error("Error uploading to S3:", error);
    throw error;
  }
};

export const hexToRgbColor = (hex: string, alpha = 1): Color.RgbColor => {
  const sanitizedHex = hex.replace("#", "");
  const bigint = parseInt(sanitizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return { r, g, b, alpha };
};

export const getThemeButton = (
  variant: "primary" | "secondary" | "tertiary",
  primaryColor: string,
  hoverColor: string,
): ButtonAppearance => {
  let base: ButtonAppearance;

  if (variant === "primary") {
    base = Buttons.Functional.Primary.Large;
  } else if (variant === "secondary") {
    base = Buttons.Functional.Secondary.Large;
  } else {
    base = Buttons.Functional.Tertiary.Large;
  }

  const isPrimary = variant === "primary";
  const isTertiary = variant === "tertiary";

  const foregroundColor = isPrimary ? hexToRgbColor("#FFFFFF") : hexToRgbColor(primaryColor);
  const backgroundNormal = isPrimary ? hexToRgbColor(primaryColor) : { r: 0, g: 0, b: 0, alpha: 0 };
  const borderColor = isTertiary ? undefined : hexToRgbColor(primaryColor);

  const getBackgroundForState = (state: "hover" | "pressed") => {
    if (isPrimary) return Color.withAlpha(hexToRgbColor(hoverColor), 1);
    if (isTertiary) return { r: 0, g: 0, b: 0, alpha: 0 };
    return Color.withAlpha(hexToRgbColor(hoverColor), 0.1);
  };

  return {
    ...base,
    normal: {
      ...base.normal,
      foregroundColor,
      backgroundColor: backgroundNormal,
      borderColor,
      textUnderline: false,
    },
    hover: {
      ...base.hover,
      foregroundColor,
      backgroundColor: getBackgroundForState("hover"),
      borderColor,
      textUnderline: false,
    },
    pressed: {
      ...base.pressed,
      foregroundColor,
      backgroundColor: getBackgroundForState("pressed"),
      borderColor,
      textUnderline: false,
    },
    disabled: {
      ...base.disabled,
      foregroundColor: hexToRgbColor(primaryColor),
      textUnderline: false,
    },
  };
};
